import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Check, Info, Settings, Zap, ShieldCheck, Play, Tv, Wifi, Smartphone, Layout, List } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Como Configurar IPTV Passo a Passo", href: "/blog/como-configurar-iptv-passo-a-passo" }
  ];

  const faq = [
    {
      question: "Qual o primeiro passo para configurar?",
      answer: "O primeiro passo é garantir que seu dispositivo esteja conectado à internet e que você tenha o aplicativo oficial da P2 PRO ou um player compatível instalado."
    },
    {
      question: "Onde encontro meu usuário e senha?",
      answer: "Seus dados de acesso são gerados no momento da sua assinatura ou solicitação de teste e são enviados imediatamente pela nossa equipe de suporte via WhatsApp."
    },
    {
      question: "Posso configurar em mais de uma TV?",
      answer: "Sim, você pode configurar o mesmo acesso em vários dispositivos, mas o uso simultâneo dependerá do número de telas contratado no seu plano."
    }
  ];

  const benefits = [
    {
      title: "Rapidez na Ativação",
      description: "Saia do zero e tenha todos os canais funcionando em menos de 5 minutos seguindo este roteiro.",
      icon: <Zap className="w-6 h-6" />
    },
    {
      title: "Clareza nos Menus",
      description: "Entenda o que cada opção do seu aplicativo significa e como elas influenciam na imagem.",
      icon: <Layout className="w-6 h-6" />
    },
    {
      title: "Segurança de Acesso",
      description: "Aprenda a proteger seu login e evitar que terceiros utilizem sua assinatura indevidamente.",
      icon: <ShieldCheck className="w-6 h-6" />
    }
  ];

  const howItWorks = [
    {
      title: "Download do App",
      description: "Instale o aplicativo recomendado para o seu modelo específico de dispositivo."
    },
    {
      title: "Seleção do Servidor",
      description: "Escolha o servidor mais próximo da sua região para menor latência."
    },
    {
      title: "Autenticação",
      description: "Insira seu usuário e senha com atenção especial a letras maiúsculas."
    },
    {
      title: "Navegação",
      description: "Aguarde o carregamento das listas e comece a maratonar seus conteúdos."
    }
  ];

  const diferenciais = [
    {
      title: "Onboarding Guiado",
      description: "Este passo a passo foi validado por milhares de usuários reais da P2 PRO.",
      icon: <Info className="w-6 h-6" />
    },
    {
      title: "Suporte 24/7",
      description: "Nossa equipe está de prontidão caso você encontre qualquer dificuldade no caminho.",
      icon: <Settings className="w-6 h-6" />
    }
  ];

  const sections = [
    {
      title: "1. Introdução: O Caminho Mais Curto para o Entretenimento",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Você acabou de adquirir o melhor sinal de IPTV do Brasil, mas e agora? <strong>Como configurar IPTV passo a passo</strong> sem se perder em termos técnicos ou menus confusos? Este guia foi escrito para simplificar sua vida. Esqueça manuais complexos de 50 páginas. Aqui, vamos direto ao que importa: como colocar a imagem na tela com a máxima qualidade e no menor tempo possível.
          </p>
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Na <strong>P2 PRO</strong>, nosso objetivo é que qualquer pessoa, independentemente do nível de conhecimento tecnológico, consiga desfrutar de uma programação de elite. Desde a instalação do primeiro aplicativo até a organização dos seus canais favoritos, este roteiro será seu fiel escudeiro na jornada de transformar sua Smart TV ou Celular em uma central de mídia profissional.
          </p>
        </div>
      )
    },
    {
      title: "2. O Ritual da Primeira Configuração: O Que Não Pode Faltar",
      type: "featured" as const,
      content: (
        <div className="space-y-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Smartphone className="w-6 h-6 text-electric" /> Dispositivo Atualizado</h4>
              <p className="text-sm text-muted-foreground">Antes de começar, verifique se o sistema da sua TV ou Box está na última versão. Isso evita bugs de compatibilidade com os apps modernos.</p>
            </div>
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Wifi className="w-6 h-6 text-amber-500" /> Internet Estável</h4>
              <p className="text-sm text-muted-foreground">Para configurar, sua internet não precisa ser a mais rápida, mas precisa estar estável. Evite fazer downloads pesados durante o primeiro login.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "3. Passo a Passo Visual: Da Loja ao Play",
      content: (
        <div className="prose prose-invert max-w-none space-y-8">
          <p className="text-muted-foreground">Siga estas etapas para uma ativação bem-sucedida:</p>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">1</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Busca e Instalação</h4>
              <p className="text-muted-foreground">Abra a loja de apps e procure por players recomendados como 'XCIPTV' ou 'IBO Player'. Instale e abra o aplicativo.</p>
            </div>
          </div>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">2</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Entrada de Credenciais</h4>
              <p className="text-muted-foreground">Escolha a opção 'Login with API' ou 'Xtream Codes'. Digite a URL do Servidor, seu Usuário e sua Senha exatamente como recebeu no WhatsApp.</p>
            </div>
          </div>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">3</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Sincronização de Lista</h4>
              <p className="text-muted-foreground">Aguarde a barra de progresso terminar. O app estará baixando a grade de canais e as capas de filmes. Não desligue a TV nesta etapa.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "4. Dicas de Ouro para sua Primeira Semana",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-muted-foreground mb-6">Muitos iniciantes desistem por pequenos erros de configuração. Siga estas dicas:</p>
          <ul className="space-y-4">
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Organize por 'Favoritos'</h5>
                <p className="text-sm text-muted-foreground">Em listas gigantes como a nossa (mais de 80 mil conteúdos), favoritar seus 10 canais principais economiza muito tempo.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Configure o EPG (Guia)</h5>
                <p className="text-sm text-muted-foreground">Certifique-se de que o Guia de Programação está atualizando. Isso permite que você veja o que está passando em cada canal antes de abrir.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Info className="w-6 h-6 text-amber-500 mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Teste de Qualidade</h5>
                <p className="text-sm text-muted-foreground">Se um canal 4K travar, teste a versão HD dele. Às vezes, o gargalo está no Wi-Fi da TV e não no servidor.</p>
              </div>
            </li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <div className="relative">
      <SEOPage
        category="Guias Dispositivos"
        title="Como Configurar IPTV Passo a Passo: Guia Rápido 2026"
        subtitle="Saia do zero e tenha milhares de canais na sua tela em menos de 5 minutos com nosso manual simplificado."
        heroImage="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1200"
        introParagraphs={[
          "Bem-vindo ao futuro da televisão. Se você chegou até aqui, é porque percebeu que a TV convencional já não atende mais suas necessidades de qualidade, preço e variedade. O IPTV é a solução definitiva, mas sabemos que a primeira configuração pode gerar dúvidas. 'Onde eu coloco a senha?', 'Qual app eu baixo?' – estas são as perguntas que responderemos agora.",
          "Neste guia prático, eliminamos toda a 'encheção de linguiça' e focamos no que realmente importa: a imagem na sua tela. Na P2 PRO, desenvolvemos um ecossistema que é intuitivo por natureza, mas um empurrãozinho profissional nunca é demais para garantir que tudo rode com a estabilidade de elite que você merece.",
          "Prepare-se para transformar a sua sala em um verdadeiro cinema particular. O mundo do IPTV é vasto e emocionante, e com este passo a passo, você estará navegando por ele em poucos minutos."
        ]}
        breadcrumbs={breadcrumbs}
        faq={faq}
        benefits={benefits}
        howItWorks={howItWorks}
        diferenciais={diferenciais}
        sections={sections}
        ctaTitle="🎁 Quer um Teste Guiado pela Nossa Equipe?"
        ctaDescription="A P2 PRO oferece suporte total para sua primeira configuração. Não quebre a cabeça sozinho, nós te ajudamos."
        ctaButtonText="Solicitar Teste Assistido"
      />
    </div>
  );
};

export const Route = createFileRoute("/blog/como-configurar-iptv-passo-a-passo")({
  head: () => ({
    meta: [
      { title: "Como Configurar IPTV Passo a Passo: Guia Completo 2026" },
      { name: "description", content: "Aprenda a configurar seu IPTV de forma simples e rápida. Passo a passo detalhado para Smart TVs, Celulares e TV Box com suporte a 4K." },
    ],
  }),
  component: ArticlePage,
});
